// packages/api-client/src/services/files.ts
import { getApiClient } from '../../client';
import { handleApiError } from '../../utils/handleApiError';
import { type UploadedFileResponse, FilePurposeEnum } from '@dopomogai/types';

type ServiceResponse<T> = Promise<T | import('@dopomogai/types').ApiErrorResponse>;

const upload = (
  file: File,
  purpose: FilePurposeEnum = FilePurposeEnum.AgentInput,
  agentId?: string,
  logicalPath?: string, // optional logical path for KB uploads (HEAD linkage done separately)
  sha256?: string, // optional for server-side dedup
): ServiceResponse<UploadedFileResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('purpose', purpose);
  if (logicalPath) formData.append('logical_path', logicalPath);
  if (sha256) formData.append('sha256', sha256);

  const query = agentId ? `?agent_id=${agentId}` : '';

  return getApiClient()
    .post(`/files/upload${query}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((res) => res.data)
    .catch((err) => handleApiError(err, 'uploading file'));
};

const list = (purpose: FilePurposeEnum, limit = 20, offset = 0): ServiceResponse<UploadedFileResponse[]> => {
  return getApiClient()
    .get('/files', { params: { purpose, limit, offset } })
    .then((res) => res.data)
    .catch((err) => handleApiError(err, 'listing files'));
};

const getMetadata = (fileId: string): ServiceResponse<UploadedFileResponse> => {
  return getApiClient()
    .get(`/files/${fileId}`)
    .then((res) => res.data)
    .catch((err) => handleApiError(err, 'fetching file metadata'));
};

/**
 * Batch metadata hydration to avoid N+1 fetches in message lists.
 * POST /files/metadata/batch
 */
const getMetadataBatch = (ids: string[]): ServiceResponse<{ items: UploadedFileResponse[] }> => {
  return getApiClient()
    .post(`/files/metadata/batch`, { ids })
    .then((res) => res.data)
    .catch((err) => handleApiError(err, 'batch fetching file metadata'));
};

const remove = (fileId: string): ServiceResponse<void> => {
  return getApiClient()
    .delete(`/files/${fileId}`)
    .then(() => undefined)
    .catch((err) => handleApiError(err, 'deleting file'));
};

const getDownloadUrl = (fileId: string): string => {
  const client = getApiClient();
  return `${client.defaults.baseURL}/files/${fileId}/download`;
};

export const filesService = {
  upload,
  list,
  getMetadata,
  getMetadataBatch,
  remove,
  getDownloadUrl,
};